import clsx from "clsx";
import { ElementType, FC, ReactElement, useEffect, useState } from "react";
import toast, { Renderable } from "react-hot-toast";
import { Tooltip } from "react-tooltip";
import {
  AccountProvider,
  IncorrectNetworkError,
  InsufficientAllowanceError,
  Msg,
  MsgErc20IncreaseAllowance,
  networkLabel,
  networkTxLink,
  Signer,
  Simulation,
  translateError,
  TxError,
  TxResult,
} from "rujira.js";
import network from "../../assets/images/network.gif";
import lock from "../../assets/images/unlock.gif";
import { i18n } from "../../i18n";
import { Warning } from "../notices/Warning";
import { Decimal } from "../numbers/Decimal";
import { Button, ButtonProps } from "./Button";

export type TxButtonProps<
  P,
  T extends ElementType = "button",
> = ButtonProps<T> & {
  accountProvider: AccountProvider<P>;
  signer: Signer;
  msg: Msg | null;
  SimulationComponent?: ElementType<{
    accountProvider: AccountProvider<P>;
    onAllowanceIncrease: () => void;
    signer: Signer;
    simulation?: Simulation;
    error?: Error;
    msg: Msg | null;
  }>;
  onError?: (err: any) => void;
  onSuccess?: (res: TxResult) => void;
  hideSimulation?: boolean;
  toastOpts?: {
    loading?: Renderable;
    success?: Renderable | ((res: TxResult) => Renderable);
    error?: Renderable | ((err: any) => Renderable);
  };
};

export const TxButton = <P,>({
  accountProvider,
  signer,
  msg,
  onSuccess,
  onError,
  SimulationComponent = DefaultSimulationComponent,
  children,
  toastOpts = {},
  className,
  hideSimulation,
  onClick,
  ...rest
}: TxButtonProps<P>): ReactElement => {
  const { simulate, signAndBroadcast } = signer;
  const [simulation, setSimulation] = useState<Simulation>();
  const [simulationError, setSimulationError] = useState<Error>();
  const { loading, success, error } = toastOpts;

  const doSimulate = (msg: Msg | null) => {
    setSimulationError(undefined);
    setSimulation(undefined);
    msg &&
      simulate &&
      simulate(msg)
        .then(setSimulation)
        .catch((err) => {
          console.error(err);
          setSimulationError(err);
          onError && onError(new TxError(msg, err, "simulation"));
        });
  };

  useEffect(() => {
    doSimulate(msg);
  }, [msg]);

  const Sign = (e: any) => {
    if (!simulation) throw new Error(`Simulation required`);
    if (!msg) throw new Error(`Msg required`);
    if (!signAndBroadcast) throw new Error(`signAndBroadcast unavailable`);

    if (onClick) onClick(e);

    const p = signAndBroadcast(simulation, msg)
      .then((res) => {
        onSuccess && onSuccess(res);
        return res;
      })
      .catch((err) => {
        console.error(err);
        onError && onError(new TxError(msg, err, "signAndBroadcast"));
        throw err;
      });

    toast.promise(p, {
      loading: loading || i18n.t("Submitting Transaction"),
      success: success || successHandler,
      error:
        error ||
        ((err: any) => {
          console.error(err);
          const defaultError = "Error Submitting Transaction";
          switch (typeof err) {
            case "string":
              return i18n.t(translateError(err) || defaultError);
            case "object":
              return i18n.t(
                "message" in err
                  ? translateError(err.message) || defaultError
                  : defaultError
              );

            default:
              return i18n.t(defaultError);
          }
        }),
    });
  };

  const disabled = !simulation || !msg || !!simulationError || rest.disabled;

  return (
    <>
      <Button
        {...rest}
        onClick={Sign}
        className={clsx({
          "button--grey": disabled,
          [`${className}`]: className,
        })}
        disabled={disabled}
        data-tooltip-id="tx-button-tip"
        data-tooltip-html={
          simulationError && simulationError.message !== ""
            ? `<p class="fs-12 lh-16 fw-400 w-36 mb-0.5">${translateError(simulationError.message)}</p>`
            : undefined
        }>
        {children}
      </Button>
      {hideSimulation ? null : msg ? (
        <SimulationComponent
          accountProvider={accountProvider}
          onAllowanceIncrease={() => {
            doSimulate(msg);
          }}
          signer={signer}
          simulation={simulation}
          error={simulationError}
          msg={msg}
        />
      ) : (
        <span>&nbsp;</span>
      )}
    </>
  );
};

export const TxButtonTip = () => (
  <Tooltip
    id="tx-button-tip"
    className="tooltip"
    float={true}
    style={{ zIndex: 201 }}
  />
);
export interface SimulationComponentProps<P> {
  accountProvider: AccountProvider<P>;
  onAllowanceIncrease: () => void;
  signer: Signer;
  simulation?: Simulation;
  error?: Error;
  msg: Msg | null;
}

const DefaultSimulationComponent = <P,>({
  accountProvider,
  onAllowanceIncrease,
  simulation,
  error,
  signer,
}: SimulationComponentProps<P>): ReactElement => {
  if (error instanceof IncorrectNetworkError) {
    return (
      <IncorrectNetworkWarning
        accountProvider={accountProvider}
        error={error}
      />
    );
  }

  if (error instanceof InsufficientAllowanceError) {
    return (
      <InsufficientAllowanceWarning
        error={error}
        signer={signer}
        onSuccess={onAllowanceIncrease}
      />
    );
  }

  return (
    <small className="fs-12 text-center mt-1 color-grey block">
      <>
        {i18n.t("Network fee")}{" "}
        {simulation ? (
          <Decimal
            amount={simulation.amount}
            decimals={simulation.decimals}
            symbol={simulation.symbol}
            className="color-white"
          />
        ) : (
          <span>calculating...</span>
        )}
      </>
    </small>
  );
};

const IncorrectNetworkWarning = <P,>({
  accountProvider,
  error,
}: {
  accountProvider: AccountProvider<P>;
  error: IncorrectNetworkError;
}): ReactElement => {
  const target =
    accountProvider.accounts?.find(
      (a) =>
        a.provider === accountProvider.selected?.provider &&
        a.network === error.required
    ) || accountProvider.accounts?.find((a) => a.network === error.required);
  return target ? (
    <button
      className="transparent w-full"
      onClick={() => {
        accountProvider.select(target);
      }}>
      <Warning className="warning--sm mt-2 condensed flex ai-c" color="orange">
        {/* <ExclamationCircle className="color-orange" /> */}
        <img src={network} alt="lock" />
        <div className="text-left">
          Incorrect Network. Click to select the{" "}
          <span className="color-white">{networkLabel(error.required)}</span>{" "}
          network
        </div>
      </Warning>
    </button>
  ) : (
    <span className="transparent w-full">
      <Warning className="warning--sm mt-2 condensed flex ai-c" color="orange">
        {/* <ExclamationCircle className="color-orange" /> */}
        <img src={network} alt="network" />
        <div className="text-left">
          Incorrect Network.{" "}
          <span className="color-white">{error.required}</span> wallet required
        </div>
      </Warning>
    </span>
  );
};

const InsufficientAllowanceWarning: FC<{
  signer: Signer;
  error: InsufficientAllowanceError;
  onSuccess: () => void;
}> = ({ error, signer, onSuccess }) => {
  const { simulate, signAndBroadcast } = signer;
  const msg = new MsgErc20IncreaseAllowance(error);

  const onClick = () => {
    if (!simulate || !signAndBroadcast) throw new Error(`Signer unavailable`);
    const p = simulate(msg)
      .then((simulation) => signAndBroadcast(simulation, msg))
      .then((res) => {
        onSuccess();
        return res;
      });

    toast.promise(p, {
      loading: i18n.t("Increasing Allowance"),
      success: i18n.t("Success"),
      error: (err: any) =>
        i18n.t(translateError(err.message) || "Error Submitting Transaction"),
    });
  };

  return (
    <button onClick={onClick} className="transparent w-full pointer">
      <Warning className="warning--sm mt-2 condensed flex ai-c" color="orange">
        {/* <ExclamationCircle className="color-orange" /> */}
        <img src={lock} alt="lock" />
        <div className="text-left">
          Click to increase allowance.{" "}
          <span className="color-white">
            <Decimal
              amount={error.current}
              decimals={error.asset.decimals}
              symbol={error.asset.symbol}
            />
          </span>{" "}
          available,{" "}
          <span className="color-white">
            <Decimal
              amount={error.required}
              decimals={error.asset.decimals}
              symbol={error.asset.symbol}
            />
          </span>{" "}
          required.
        </div>
      </Warning>
    </button>
  );
};

const successHandler = (res: TxResult): Renderable =>
  res.label ? (
    <p>{res.label}</p>
  ) : (
    <p>
      {i18n.t("Transaction Succeeded")}
      <br />
      <a
        href={networkTxLink(res)}
        target="_blank"
        className="color-white no-underline fs-12">
        {res.txHash.slice(0, 8) + "..." + res.txHash.slice(-8)}
      </a>
    </p>
  );
